import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSiteBranding } from '../hooks/useSiteBranding';
import { telHref } from '../lib/mainSiteBranding';
import { POS_ROLES } from '../lib/roles';
import { List, X, User, SignOut, Phone, Wrench, MapPin, Calendar, FileText, CaretDown, Desktop, ShoppingCart, Package, DeviceMobileCamera } from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

export function Header() {
  const { user, logout, siteClient } = useAuth();
  const { phone, logoUrl, tenantName } = useSiteBranding();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isClientSite = !!siteClient;  // true when accessed via a tenant's custom domain

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = [
    { name: 'Services', href: '/services', icon: Wrench },
    { name: 'Locations', href: '/locations', icon: MapPin },
    { name: 'Book Repair', href: '/booking', icon: Calendar },
    { name: 'Mail-In Repair', href: '/mail-in', icon: Package },
    { name: 'Get Quote', href: '/quote', icon: FileText },
    { name: 'Sell Your Device', href: '/trade-in', icon: DeviceMobileCamera },
    { name: 'Track Repair', href: '/track', icon: Phone },
    { name: 'Shop', href: '/shop', icon: ShoppingCart },
  ];

  return (
    <header className="bg-[#050505]/80 backdrop-blur-xl border-b border-[#262626] sticky top-0 z-40" data-testid="header">
      <div className="container-padding">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src={logoUrl} 
              alt={tenantName} 
              className="h-24 w-auto"
              data-testid={isClientSite && siteClient?.branding?.logo_url ? "header-tenant-logo" : "header-default-logo"}
            />
            {isClientSite && siteClient?.name && !siteClient?.branding?.logo_url && (
              <span className="hidden md:inline-block text-white font-bold text-base border-l border-[#262626] pl-3 ml-1" data-testid="client-brand-name">
                {siteClient.name}
              </span>
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-[#A3A3A3] hover:text-white transition-colors text-sm font-medium uppercase tracking-wide"
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Auth Section */}
          <div className="flex items-center gap-3">
            {/* Click-to-call */}
            <a href={telHref(phone)} className="hidden md:flex items-center gap-2 text-white bg-[#E3000F] hover:bg-[#FF3B30] px-4 py-2 transition-colors" data-testid="header-call-btn">
              <Phone size={16} weight="bold" />
              <span className="text-sm font-bold">{phone}</span>
            </a>
            <a href={telHref(phone)} className="md:hidden flex items-center justify-center w-10 h-10 bg-[#E3000F] hover:bg-[#FF3B30] transition-colors" data-testid="header-call-btn-mobile">
              <Phone size={18} weight="bold" className="text-white" />
            </a>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 text-white hover:bg-[#1A1A1A]"
                    data-testid="user-menu-trigger"
                  >
                    <User size={20} weight="bold" />
                    <span className="hidden sm:block">{user.name}</span>
                    <CaretDown size={14} weight="bold" className="text-[#A3A3A3]" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-[#111111] border-[#262626]">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="cursor-pointer" data-testid="dashboard-link">
                      My Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/shop/orders" className="cursor-pointer" data-testid="my-orders-link">
                      <Package size={16} className="mr-2" />
                      My Orders
                    </Link>
                  </DropdownMenuItem>
                  {POS_ROLES.includes(user.role) && (
                    <>
                      <DropdownMenuSeparator className="bg-[#262626]" />
                      <DropdownMenuItem asChild>
                        <Link to="/pos" className="cursor-pointer" data-testid="pos-link">
                          <Desktop size={16} className="mr-2" />
                          POS Portal
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  {user.role === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer text-[#E3000F]" data-testid="admin-link">
                        <Desktop size={16} className="mr-2" />
                        Admin Portal
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-[#262626]" />
                  <DropdownMenuItem 
                    onClick={handleLogout} 
                    className="cursor-pointer text-[#E3000F]"
                    data-testid="logout-btn"
                  >
                    <SignOut size={16} className="mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    className="text-white hover:bg-[#1A1A1A] hidden sm:flex"
                    data-testid="login-btn"
                  >
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    className="bg-[#E3000F] hover:bg-[#FF3B30] text-white font-bold uppercase tracking-wide"
                    data-testid="register-btn"
                  >
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-white"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} weight="bold" /> : <List size={24} weight="bold" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-[#262626]" data-testid="mobile-nav">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 py-3 text-[#A3A3A3] hover:text-white transition-colors"
              >
                <link.icon size={20} />
                {link.name}
              </Link>
            ))}
            {!user && (
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 py-3 text-[#A3A3A3] hover:text-white transition-colors"
              >
                <User size={20} />
                Login
              </Link>
            )}
          </nav>
        )}
      </div>
    </header>
  );
}
